// Forms section (R-F1, R-F2): forms of the chosen event in the core's second column; the open form — its top line, then the builder,
// settings and the responses.

import { useEffect, useMemo, useState } from 'react';
import { useScreenSearch } from '../shell/screen-search.ts';
import { uuidv7, type FormDef, type FormTypeDef } from '@confdeck/core-shared';
import { navigate, useLocation } from '../shell/router.ts';
import { useScope } from '../shell/scope.tsx';
import { useMeta, useSync } from '../sync/react.tsx';
import { ListBody, ListEmpty, ListHead, ListReader, ListRow } from '../ui/ListReader.tsx';

import { TopBar, TopTitleInput } from '../ui/TopBar.tsx';
import { Button, Chip, Menu, MenuContent, MenuItem, MenuTrigger, Tabs, useToast } from '../ui/primitives.tsx';
import { Icon } from '../ui/icons.tsx';
import { FormBuilder } from './FormBuilder.tsx';
import { FormResponses } from './FormResponses.tsx';
import { FormSettingsPane } from './FormSettingsPane.tsx';
import s from './forms.module.css';
import b from '../ui/builder.module.css';

export const STATUS: Record<FormDef['status'], { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'gray' }, published: { label: 'Принимает ответы', color: 'green' }, closed: { label: 'Закрыта', color: 'amber' },
};

export function FormsScreen() {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const [scopeId] = useScope();
  const { search } = useLocation();
  const types: FormTypeDef[] = client.header?.formTypes ?? [];
  const forms = useMemo(() => meta.forms().filter((f) => !f.archivedAt && (!scopeId || f.scopeId === scopeId || !f.scopeId)), [meta, scopeId, meta.forms().length]);
  const selectedId = search.get('form') ?? forms[0]?.id ?? null;
  const form = selectedId ? meta.form(selectedId) : undefined;
  const select = (id: string) => navigate(`/s/forms?form=${id}`, { replace: true });

  const create = async (t: FormTypeDef) => {
    const id = uuidv7();
    const scope = scopeId ? client.store.record(scopeId) : undefined;
    const title = scope ? String(scope.data[meta.type(scope.typeId)?.titleField ?? 'name'] ?? '') : '';
    const r = await client.run('form.save', { id, name: title ? `${t.label} — ${title}` : t.label, formType: t.key, scopeId: scopeId ?? undefined });
    if (r.ok) select(id); else toast({ kind: 'error', text: r.error.message });
  };

  // The global search looks here first (R-U23): the forms of the chosen event by their name.
  useScreenSearch((query) => {
    const needle = query.trim().toLowerCase();
    return forms.filter((f) => f.name.toLowerCase().includes(needle))
      .map((f) => ({ id: f.id, label: f.name, hint: STATUS[f.status].label.toLowerCase(), icon: 'form', run: () => select(f.id) }));
  });
  return (
    <ListReader selection={selectedId} listLabel="Формы" readerLabel="Форма" list={(
      <>
        <ListHead title="Формы"
          add={{ label: 'Форма', menu: types.map((t) => ({ key: t.key, label: t.label, onSelect: () => void create(t) })) }} />
        {forms.length ? (
          <ListBody label="Формы">
            {forms.map((f) => (
              <ListRow key={f.id} selected={f.id === selectedId} onClick={() => select(f.id)} dot={f.status === 'published' ? 'on' : 'off'}
                title={f.name} sub={`${types.find((t) => t.key === f.formType)?.label ?? f.formType} · ${STATUS[f.status].label.toLowerCase()}`} />
            ))}
          </ListBody>
        ) : <ListEmpty icon="form" text={scopeId ? 'У этого события ещё нет форм' : 'Форм пока нет'}
          hint={scopeId ? 'Создайте форму кнопкой «+» или действием события «Создать формы события».' : undefined} />}
      </>
    )} reader={form ? <FormEditor key={form.id} form={form} type={types.find((t) => t.key === form.formType)} /> : <><TopBar /><ListEmpty icon="form" text="Выберите или создайте форму" /></>} />
  );
}

type Tab = 'fields' | 'settings' | 'responses';

function FormEditor({ form, type }: { form: FormDef; type: FormTypeDef | undefined }) {
  const client = useSync();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('fields');
  const [name, setName] = useState(form.name);
  useEffect(() => setName(form.name), [form.name]);
  const link = client.header?.siteUrl ? `${client.header.siteUrl}/f/${form.id}` : null;
  const run = async (command: string, input: unknown, ok?: string) => {
    const r = await client.run(command, input);
    if (!r.ok) toast({ kind: 'error', text: r.error.message });
    else if (ok) toast({ kind: 'info', text: ok });
    return r.ok;
  };
  const rename = () => { if (name.trim() && name !== form.name) void run('form.save', { id: form.id, name: name.trim(), formType: form.formType, scopeId: form.scopeId ?? undefined }); };
  /** A copy of the form (R-F1): a draft of its own that opens right away, like a form just created. */
  const copy = async () => {
    const newId = uuidv7();
    const r = await client.run('form.copy', { id: form.id, newId });
    if (!r.ok) { toast({ kind: 'error', text: r.error.message }); return; }
    toast({ kind: 'info', text: 'Форма скопирована' });
    navigate(`/s/forms?form=${newId}`, { replace: true });
  };
  const st = STATUS[form.status];
  const automationId = type?.process ? client.store.meta.automations().find((a) => a.trigger.type === 'form.submitted' && a.trigger.formId === form.id)?.id : undefined;

  return (
    <section className={b.editor}>
      <TopBar start={<>
        <TopTitleInput value={name} onChange={setName} onCommit={rename} label="Название формы" />
        <Chip color={st.color} dot className={b.noShrink}>{st.label}</Chip>
        <span className={b.typeNote} title={type?.description}>{type?.label}</span>
      </>}
        actions={[
          ...(link && form.status !== 'draft' ? [{ key: 'open', label: 'Открыть', icon: 'external', onClick: () => window.open(link, '_blank') }] : []),
          ...(link ? [{ key: 'link', label: 'Ссылка', icon: 'copy', onClick: () => { void navigator.clipboard?.writeText(link); toast({ kind: 'info', text: 'Ссылка скопирована' }); } }] : []),
          ...(automationId ? [{ key: 'flow', label: 'Сценарий', icon: 'zap', onClick: () => navigate(`/a/${automationId}`) }] : []),
          form.status === 'published'
            ? { key: 'close', label: 'Закрыть приём', icon: 'lock', onClick: () => void run('form.close', { id: form.id }, 'Приём ответов закрыт') }
            : { key: 'publish', label: 'Опубликовать', icon: 'tick', primary: true, onClick: () => void run('form.publish', { id: form.id }, 'Форма принимает ответы') },
        ]}
        more={<>
          <MenuItem icon="copy" onSelect={() => void copy()}>Копировать</MenuItem>
          <MenuItem icon="archive" danger onSelect={() => void run('form.archive', { id: form.id }, 'Форма в архиве')}>В архив</MenuItem>
        </>} />
      <Tabs bar label="Форма" value={tab} onChange={setTab} items={[{ key: 'fields', label: 'Поля' }, { key: 'settings', label: 'Настройки' }, { key: 'responses', label: 'Ответы' }]} />
      <div className={b.scroll}>
      {tab === 'fields' && <FormBuilder form={form} type={type} />}
      {tab === 'settings' && <FormSettingsPane form={form} />}
      {tab === 'responses' && <FormResponses form={form} />}
      {form.status === 'published' && tab === 'fields' && <p className={s.note}><Icon name="sparkles" size={14} />Форма уже принимает ответы: изменения полей увидят новые посетители, прошлые ответы сохраняются как были.</p>}
      </div>
    </section>
  );
}
